import { FunctionComponent } from 'react';
import type { WizardStepTracker } from './wizard-step-tracker';
import type { WizardStepController } from './wizard-step-controller';
import type { WizardStepBaseProps } from './wizard-step-base-props';

export interface WizardStepMeta<Data = object> {
  path: string;
  component?: FunctionComponent<WizardStepBaseProps>;
  resolveComponent?: () => FunctionComponent<WizardStepBaseProps>;
  footerComponent?: FunctionComponent;
  isFinal?: boolean;
  resolve?: ResolveStepData<Data>;
  params?: WizardStepParam;
  nextStep?: string;
  getNextStep?: (
    tracker: WizardStepTracker,
    self: WizardStepMeta<Data>,
    currentSkipped: boolean
  ) => string | Promise<string> | undefined;
}

export interface ResolveStepData<T> {
  [name: string]: WizardStepResolve<T> | T;
}

export interface WizardStepResolve<T> {
  resolve(tracker?: WizardStepTracker, params?: WizardStepParam): Promise<T> | T;
}

export interface WizardStepTrackerStep<Data extends object = object> {
  data?: Data;
  path: string;
  next?: string;
  params?: WizardStepParam;
  title?: string;
  dirty?: boolean;
  complete?: boolean;
}

export interface WizardStepTrackerSnapshot {
  [name: string]: string | number | boolean | symbol | object | null | undefined | never | unknown;
}
export interface WizardStepParam {
  [name: string]: string | number | boolean | symbol | object | null | undefined | never | unknown;
}

export interface WizardStepTree {
  id: string;
  title: string;
  for: string;
  children: WizardStepTree[];
  Extra?: FunctionComponent<object>;
  extraProps?: object;
}

export interface WizardStepTreeSnapshot {
  segment: string;
  children: WizardStepTreeSnapshot[];
  complete?: boolean;
  active?: boolean;
}

export interface WizardStorageData<Data extends object = object> {
  data: Data;
  steps: WizardStepTrackerStep<Data>[];
  version: WizardVersion;
}

export interface WizardStorage<Data extends object = object> {
  getStoredData(key: string): WizardStorageData<Data> | null;
  store(key: string, data: WizardStorageData<Data>): void;
}

export interface WizardOptions {
  trackingOptions?: WizardStepTrackerOptions;
}

export interface WizardStepTrackerOptions {
  storageKey?: string;
  trackingStrategy?: WizardStepTrackingStrategy;
}

export enum WizardStepTrackingStrategy {
  DISCARD_ALWAYS = 'DISCARD_ALWAYS',
  DISCARD_ON_DIRTY = 'DISCARD_ON_DIRTY'
}

export enum WizardState {
  INIT = 'INIT',
  IDLE = 'IDLE',
  LOADING_STEP = 'LOADING_STEP',
  ERRORED = 'ERRORED'
}

export interface WizardStateWrapper {
  state: WizardState;
  error?: string | object;
}

export interface WizardTimeTravelData<Data extends object = object> {
  steps: WizardStepTrackerStep<Data>[];
  data?: object;
  skipUpdateQueryParams?: boolean;
  next?: boolean;
}

export enum WizardVersion {
  V2 = 'V2'
}

export interface WizardCurrentStep {
  component: FunctionComponent<WizardStepBaseProps>;
  footerComponent?: FunctionComponent;
  footerProps?: object;
  beforeUnload?: VoidFunction;
  path: string;
  controller: WizardStepController;
}
